export interface ApiErrorBody {
  readonly code?: string | null;
  readonly message?: string | null;
  readonly fields?: Readonly<Record<string, string>> | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly fieldErrors: ReadonlyMap<string, string>;

  constructor(
    message: string,
    status: number,
    code: string | null,
    fieldErrors: ReadonlyMap<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

function toFieldErrors(fields: unknown): ReadonlyMap<string, string> {
  if (fields === null || typeof fields !== 'object' || Array.isArray(fields)) {
    return new Map<string, string>();
  }

  const entries = Object.entries(fields as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );

  return new Map<string, string>(entries);
}

function readBody(value: unknown): ApiErrorBody | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }
  return value as ApiErrorBody;
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (typeof error === 'string') {
    return new ApiError(error, 0, null, new Map<string, string>());
  }

  const candidate = error as {
    status?: unknown;
    error?: unknown;
    message?: unknown;
  } | null;
  const status = typeof candidate?.status === 'number' ? candidate.status : 0;
  const body = readBody(candidate?.error);
  const message =
    body?.message ??
    (typeof candidate?.message === 'string' ? candidate.message : null) ??
    'Request failed';

  return new ApiError(
    message,
    status,
    body?.code ?? null,
    toFieldErrors(body?.fields)
  );
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
